import cn from "classnames"
import { useEffect, useMemo, useRef, useState } from "react"

import { Button, Dropdown, Search } from "@/atoms"
import { useToggle } from "@/hooks/useToggle"
import { DownOutlined } from "@/icons"
import {
  APPLY_FILTER_SYMBOL,
  DATA_ID_SMART_SEARCH,
  INPUT_SMART_SEARCH_ID,
  ROW_TEMPLATE_FILTER_ID
} from "@/organisms/SmartSearch/const/symbol.constants"
import { getEnterEvent } from "@/organisms/SmartSearch/helpers/event.helper"
import { ButtonsFilter } from "../ButtonsFilter"

import styles from "./DropdownFilter.module.scss"

export const DropdownFilter = ({
  children,
  disabled,
  changed,
  allowApply,
  allowSearch,
  isPlaceholder,
  onOpenChange,
  onOk,
  onClear,
  clearDisabled,
  okHide,
  okDisabled,
  menuItems: menuItemsProp
}: any) => {
  const { value: open, toggle, setValue } = useToggle()
  const [search, setSearch] = useState("")
  const shouldApplyRef = useRef(false)
  const ref = useRef<HTMLDivElement>(null)

  const menuItems = useMemo(() => {
    const copyMenuItems = [...menuItemsProp].filter(
      ({ key }: { key: string }) => key.indexOf(search) !== -1
    )

    if (allowSearch && menuItemsProp.length > 6) {
      if (copyMenuItems.length > 0) {
        copyMenuItems.unshift({
          type: "divider"
        })
      }

      copyMenuItems.unshift({
        className: styles.sectionControl,
        label: <Search className={styles.search} onSearch={setSearch} />,
        key: "search"
      })
    }

    if (allowApply) {
      if (copyMenuItems.length > 0) {
        copyMenuItems.push({
          type: "divider"
        })
      }
      copyMenuItems.push({
        className: styles.sectionControl,
        label: (
          <ButtonsFilter
            clearDisabled={clearDisabled}
            okHide={okHide}
            okDisabled={okDisabled}
            onOk={() => {
              toggle()

              onOk?.()
            }}
            onClear={onClear}
          />
        ),
        key: "apply"
      })
    }

    return copyMenuItems
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItemsProp, search, toggle])

  useEffect(() => {
    if (open || !changed) return

    const el = ref.current
      ?.closest(`[${DATA_ID_SMART_SEARCH}="${ROW_TEMPLATE_FILTER_ID}"]`)
      ?.querySelector(
        `[${DATA_ID_SMART_SEARCH}="${INPUT_SMART_SEARCH_ID}"]`
      ) as HTMLElement | null

    if (!el) return

    if (shouldApplyRef.current) {
      const kbEvent = getEnterEvent(APPLY_FILTER_SYMBOL)
      el.dispatchEvent(kbEvent)
      shouldApplyRef.current = false

      return
    }

    el.focus()

    queueMicrotask(() => {
      const range = document.createRange()

      range.selectNodeContents(el)
      range.collapse(false)

      const selection = window.getSelection()

      if (!selection) return

      selection.removeAllRanges()
      selection.addRange(range)

      el.click()
    })
  }, [open, changed])

  return (
    <Dropdown
      disabled={disabled}
      rootClassName={styles.root}
      trigger={[open ? "hover" : "click"]}
      menu={{
        items: menuItems,
        onClick: (e) => {
          e.domEvent.stopPropagation()
        },
        onKeyUp: (e) => {
          if (e.code === "Enter" && changed) {
            shouldApplyRef.current = true
            setValue(false)
          }
        }
      }}
      open={open}
      onOpenChange={(open, info) => {
        if (info.source === "trigger") setValue(open)

        onOpenChange?.(open, info)
      }}
    >
      <div ref={ref}>
        <Button
          disabled={disabled}
          className={cn(styles.button, disabled && styles.disabled)}
          onClick={toggle}
        >
          <div
            className={cn(
              styles.buttonContainer,
              isPlaceholder && styles.placeholder
            )}
          >
            {children}
            <DownOutlined className={styles.dropdownIcon} />
          </div>
        </Button>
      </div>
    </Dropdown>
  )
}
